import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate,
  getStatusChipStyle
} from './portalShared'

const DEFAULT_CENTER = [33.55, -112.0]

const useStyles = makeStyles((theme) => ({
  controlsCard: {
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  listCard: {
    borderRadius: 8,
    border: '1px solid rgba(205, 216, 209, 0.9)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    background: '#fff',
    height: '100%'
  },
  mapCard: {
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  mapContainer: {
    width: '100%',
    minHeight: 680,
    height: 680,
    backgroundColor: '#f4f8f5'
  },
  cardTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  helperText: {
    color: '#5d6c64'
  },
  toolbarButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1.25)
  },
  siteTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  siteAddress: {
    color: '#42554c',
    marginTop: theme.spacing(0.5)
  },
  siteMeta: {
    color: '#5d6c64',
    marginTop: theme.spacing(0.5)
  }
}))

const assignmentViewOptions = [
  { value: 'all', label: 'All visible sites' },
  { value: 'assigned', label: 'Assigned only' },
  { value: 'unassigned', label: 'Unassigned only' }
]

const sortOptions = [
  { value: 'nextScheduledDate', label: 'Urgency / due date' },
  { value: 'siteName', label: 'Property name' },
  { value: 'address', label: 'Address' },
  { value: 'status', label: 'Status bucket' }
]

function buildMarkerIcon (assigned = false) {
  const color = assigned ? '#1b5e20' : '#8a692d'
  return L.divIcon({
    className: '',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.25);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8]
  })
}

const SubcontractorJobsites = ({ user }) => {
  const classes = useStyles()
  const isAdmin = user?.subcontractorProfile?.role === 'admin'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [assignmentView, setAssignmentView] = useState('all')
  const [sortBy, setSortBy] = useState('nextScheduledDate')
  const [viewMode, setViewMode] = useState('list')
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const markersRef = useRef([])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadJobsites = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get('/api/subcontractor/jobsites', {
        params: {
          search,
          sortBy,
          sortOrder: 'asc',
          assignmentView: isAdmin ? assignmentView : 'assigned'
        }
      })

      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
    } catch (requestError) {
      console.error('Failed to load subcontractor jobsites', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load subcontractor jobsites right now.')
    } finally {
      setLoading(false)
    }
  }, [assignmentView, isAdmin, search, sortBy])

  useEffect(() => {
    loadJobsites()
  }, [loadJobsites])

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return
    }

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 11,
      zoomControl: true
    })

    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
      attribution: '&copy; Google Maps',
      maxZoom: 20,
      minZoom: 0
    }).addTo(map)

    mapRef.current = map
    window.requestAnimationFrame(() => map.invalidateSize())

    return () => {
      map.remove()
      mapRef.current = null
      markersRef.current = []
    }
  }, [])

  useEffect(() => {
    if (!mapRef.current || viewMode !== 'map') {
      return
    }

    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    const mappedItems = items.filter(item => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
    const bounds = []

    mappedItems.forEach((item) => {
      const marker = L.marker([item.latitude, item.longitude], {
        icon: buildMarkerIcon(item.isAssigned)
      })
      marker.bindPopup(`
        <div style="min-width:220px">
          <strong>${item.siteName || 'Unnamed Site'}</strong><br/>
          ${item.address || 'No address'}<br/>
          ${item.assignedCrewName || 'Unassigned'}
        </div>
      `)
      marker.addTo(mapRef.current)
      markersRef.current.push(marker)
      bounds.push([item.latitude, item.longitude])
    })

    window.requestAnimationFrame(() => {
      if (!mapRef.current) return
      mapRef.current.invalidateSize()
      if (bounds.length) {
        mapRef.current.fitBounds(bounds, { padding: [40, 40] })
      } else {
        mapRef.current.setView(DEFAULT_CENTER, 11)
      }
    })
  }, [items, viewMode])

  const summaryCards = useMemo(() => ([
    {
      eyebrow: 'Visible Sites',
      value: summary.total || 0,
      caption: 'Unique subcontractor-visible jobsites independent of the calendar view.',
      accent: 'green'
    },
    {
      eyebrow: 'Mapped',
      value: summary.mapped || 0,
      caption: 'Sites with coordinates that can be shown on the subcontractor map.',
      accent: 'blue'
    },
    {
      eyebrow: 'Assigned',
      value: summary.assigned || 0,
      caption: 'Sites currently tied to a subcontractor crew.',
      accent: 'gold'
    },
    {
      eyebrow: 'Unassigned',
      value: summary.unassigned || 0,
      caption: 'Visible sites that still need subcontractor crew ownership.',
      accent: 'plum'
    }
  ]), [summary])

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading jobsites" message="Building the subcontractor site library from visible herbicide properties." minHeight={280} />
  }

  if (error && !items.length) {
    return (
      <PortalStateCard
        title="Jobsites unavailable"
        message={error}
        actionLabel="Retry"
        onAction={loadJobsites}
        tone="danger"
        minHeight={240}
      />
    )
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
              accent={card.accent}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={classes.controlsCard}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search jobsites or addresses"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  variant="outlined"
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              {isAdmin ? (
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Assignment View"
                    value={assignmentView}
                    onChange={(event) => setAssignmentView(event.target.value)}
                    variant="outlined"
                  >
                    {assignmentViewOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ) : null}
              <Grid item xs={12} md={4}>
                <div className={classes.toolbarButtons}>
                  <Button
                    variant={viewMode === 'list' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => setViewMode('list')}
                  >
                    List View
                  </Button>
                  <Button
                    variant={viewMode === 'map' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => setViewMode('map')}
                  >
                    Map View
                  </Button>
                  <Button variant="outlined" disabled>
                    Add Site (V2)
                  </Button>
                </div>
              </Grid>
            </Grid>

            <Box mt={2}>
              <Typography variant="body2" className={classes.helperText}>
                This page is the subcontractor site library, not the calendar. It shows the herbicide properties currently visible to this subcontractor scope. Future enhancements can add new-site intake and a DFL-admin display mode.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {viewMode === 'map' ? (
        <Card className={classes.mapCard}>
          <div ref={mapContainerRef} className={classes.mapContainer} />
        </Card>
      ) : (
        <Grid container spacing={3}>
          {items.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item._id}>
              <Card className={classes.listCard}>
                <CardContent>
                  <Typography variant="h6" className={classes.siteTitle}>
                    {item.siteName || 'Unnamed Site'}
                  </Typography>
                  <Typography variant="body2" className={classes.siteAddress}>
                    {item.address || 'No address on file'}
                  </Typography>
                  <Typography variant="body2" className={classes.siteMeta}>
                    {item.communityName || 'No community'}{item.buildingNumber ? ` • Bldg ${item.buildingNumber}` : ''}
                  </Typography>
                  <Box className={classes.chipRow}>
                    <Chip size="small" label={item.assignedCrewName || 'Unassigned'} />
                    <Chip size="small" label={item.assignedTerritoryName || 'Unassigned'} />
                    <Chip size="small" label={item.statusLabel || 'OK'} style={getStatusChipStyle(item.status)} />
                    <Chip size="small" label={`${item.visibleScheduleCount || 1} visible row${item.visibleScheduleCount === 1 ? '' : 's'}`} />
                  </Box>
                  <Box mt={1.5}>
                    <Typography variant="body2" className={classes.helperText}>
                      Next target: {formatPortalDate(item.effectiveNextDate, { fallback: 'Not scheduled' })}
                    </Typography>
                    <Typography variant="body2" className={classes.helperText}>
                      Last applied: {formatPortalDate(item.lastAppliedDate, { fallback: 'No prior application' })}
                    </Typography>
                    {item.gateCode ? (
                      <Typography variant="body2" className={classes.helperText}>
                        Gate code: {item.gateCode}
                      </Typography>
                    ) : null}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(SubcontractorJobsites)
